import { useCurrentWorkflowStore } from '@/stores/workflow/currentWorkflow';
import type { ActionItemEmits, ActionItemProps } from './ActionItem.vue';
import type { ActionItemMode } from './types';
import type { WorkflowDefinitionActionEntity } from '@/api/entity_types/workflow/WorkflowDefinition';
import { computed, ref, type EmitFn } from 'vue';
import { toast } from 'vue-sonner';
import type {
  WorkflowActionableParams,
  WorkflowActionParams,
  WorkflowScheduledActionParams,
} from '@/api/entity_types/workflow/WorkflowAction';
import { Api } from '@/api';

export type UseWorkflowActionArgs = ActionItemProps & { emit: EmitFn<ActionItemEmits> };

type ActionArgs = {
  actionParams: WorkflowActionParams;
  actionableParams: WorkflowActionableParams;
  scheduledActionParams?: WorkflowScheduledActionParams;
};

export const useWorkflowAction = ({ action, initMode, emit }: UseWorkflowActionArgs) => {
  const getSelectedActionDefinition = () => {
    if (action)
      return workflowStore.currentDefinition.actions.find(x => x.actionable_type === action.workflow_actionable_type)!;
    return workflowStore.currentDefinition.actions[0];
  };

  const workflowStore = useCurrentWorkflowStore();
  const mode = ref<ActionItemMode>(initMode);
  const selectedAction = ref<WorkflowDefinitionActionEntity>(getSelectedActionDefinition());

  const changeSelectedAction = (actionName: string) => {
    const action = workflowStore.currentDefinition.actions.find(x => x.name === actionName);
    if (!action) throw new Error('Attempted to edit a non existent action');
    selectedAction.value = action;
  };

  const createAction = async (args: ActionArgs) => {
    const errors = isValid(args);
    if (errors.length > 0) {
      toast.error('Workflow Action Error', { description: errors.join(', ') });
      return;
    }

    const { actionParams, actionableParams, scheduledActionParams } = args;

    const response = await Api.WorkflowActions.create({
      workflowId: workflowStore.currentWorkflow.id,
      actionParams,
      actionableParams,
      scheduledActionParams,
    });

    if (response.status === 'success') {
      workflowStore.pushAction(response.data);
      const description = 'Your workflow action was successfully created.';
      toast.success('Workflow Action Created', { description });
      emit('action-added');
    } else {
      const description = response.messages.join(', ') || 'Network Error';
      toast.error('Workflow Action Error', { description });
    }
  };

  const updateAction = async (args: Partial<ActionArgs>) => {
    if (!action) throw new Error('Attempting to update an action that does not exist');

    const errors = isValid(args);
    if (errors.length > 0) {
      toast.error('Workflow Action Error', { description: errors.join(', ') });
      return;
    }

    const { actionParams, actionableParams, scheduledActionParams } = args;

    const response = await Api.WorkflowActions.update({
      workflowId: workflowStore.currentWorkflow.id,
      workflowActionId: action.id,
      actionParams,
      actionableParams,
      scheduledActionParams,
    });

    if (response.status === 'success') {
      workflowStore.updateAction(response.data);
      const description = 'Your workflow action was successfully updated.';
      toast.success('Workflow Action Updated', { description });
      enableViewMode();
    } else {
      const description = response.messages.join(', ') || 'Network Error';
      toast.error('Workflow Action Error', { description });
    }
  };

  const destroyAction = async () => {
    if (!action) throw new Error('Attempting to delete an action that does not exist');

    const { workflow_actions, status } = workflowStore.currentWorkflow;
    const willBeActiveAndActionless = workflow_actions.length === 1 && status === 'active';
    if (willBeActiveAndActionless) {
      const description = 'Disable this workflow to delete the last action.';
      toast.error('Workflow Action Error', { description });
      return;
    }

    const response = await Api.WorkflowActions.destroy({
      workflowId: workflowStore.currentWorkflow.id,
      workflowActionId: action.id,
    });

    if (response.status === 'success') {
      workflowStore.removeAction(response.data);
      const description = 'Your workflow action was successfully deleted.';
      toast.success('Workflow Action Deleted', { description });
    } else {
      const description = response.messages.join(', ') || 'Network Error';
      toast.error('Workflow Action Error', { description });
    }
  };

  const isValid = (params: Partial<ActionArgs>) => {
    const errors = [];
    if (params.actionParams?.invocation === 'scheduled') {
      if (!params.scheduledActionParams) errors.push('Missing scheduled params');
      if ((params.scheduledActionParams?.delay_in_minutes || 0) <= 0)
        errors.push('Days after trigger must be in a positive number');
    }

    return errors;
  };

  const enableViewMode = () => {
    mode.value = 'view';
    emit('view-mode-enabled');
  };

  const deleteModal = computed(() => {
    const pending = action?.pending_scheduled_instance_count ?? 0;
    if (pending === 0) {
      return {
        title: `Delete ${selectedAction.value.name} Action`,
        description: 'Are you sure you want to permanently delete this action?',
      };
    } else {
      return {
        title: '⚠️ ATTENTION',
        description: `Deleting this action will CANCEL ${pending} scheduled action${pending > 1 ? 's' : ''}. Are you sure you want to permanently delete this action?`,
      };
    }
  });

  return {
    mode,
    selectedAction,
    changeSelectedAction,
    createAction,
    updateAction,
    destroyAction,
    enableViewMode,
    deleteModal,
  };
};
